import React, { useState, useEffect, useCallback } from 'react';
import { lambdaService } from '../../services/lambdaService';
import {
  SQLConnection,
  SQLConnectionInput,
  SQLJob,
  SQLJobInput,
  SQLJobRun,
  DatabaseType,
  DATABASE_TYPES,
  SCHEDULE_PRESETS,
} from '../../types/sqlIngestion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import {
  Database,
  Table2,
  Plus,
  RefreshCw,
  Play,
  Settings,
  ChevronRight,
  Search,
  Key,
  Clock,
  Download,
  AlertCircle,
  CheckCircle,
  HelpCircle,
  Eye,
  Code,
  Columns,
  Filter,
  Calendar,
  Shield,
  Server,
  Lock,
  Info,
  X,
  ChevronDown,
  History,
  Terminal,
  Loader2,
  ExternalLink
} from 'lucide-react';

// Types for table browser
interface TableInfo {
  name: string;          // Full name (schema.table) for API calls
  display_name?: string; // Display name without schema
  schema?: string;       // Schema name
  size_bytes?: number;   // Optional - only if include_size requested
  row_count?: number;    // Optional - only if include_row_count requested
}

interface ColumnInfo {
  name: string;
  type: string;
  nullable: boolean;
  default: any;
  is_datetime: boolean;
  is_numeric: boolean;
  is_primary_key: boolean;
}

type ViewMode = 'browser' | 'jobs' | 'history' | 'help';
type ImportMode = 'visual' | 'sql';

export const SQLIngestionTab: React.FC = () => {
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('browser');

  // Connection state
  const [connections, setConnections] = useState<SQLConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<SQLConnection | null>(null);
  const [showConnectionForm, setShowConnectionForm] = useState(false);

  // Table browser state
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableSchema, setTableSchema] = useState<ColumnInfo[]>([]);
  const [tableSearch, setTableSearch] = useState('');
  const [loadingTables, setLoadingTables] = useState(false);
  // Table detail options (off by default for large DB performance)
  const [includeRowCount, setIncludeRowCount] = useState(false);
  const [includeSize, setIncludeSize] = useState(false);

  // Preview state
  const [previewData, setPreviewData] = useState<Record<string, any>[]>([]);
  const [previewColumns, setPreviewColumns] = useState<string[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Import builder state
  const [importMode, setImportMode] = useState<ImportMode>('visual');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [rowLimit, setRowLimit] = useState<number | null>(null);
  const [incrementalColumn, setIncrementalColumn] = useState<string | null>(null);
  const [customSQL, setCustomSQL] = useState('');
  const [schedule, setSchedule] = useState('');
  const [jobName, setJobName] = useState('');
  const [toastCollapsed, setToastCollapsed] = useState(false);

  // Jobs state
  const [jobs, setJobs] = useState<SQLJob[]>([]);
  const [jobHistory, setJobHistory] = useState<SQLJobRun[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [editingJob, setEditingJob] = useState<SQLJob | null>(null);

  // UI state
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [runningJobId, setRunningJobId] = useState<string | null>(null);

  // Load connections on mount
  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    const result = await lambdaService.listSQLConnections();
    if (result.success && result.connections) {
      setConnections(result.connections);
      if (result.connections.length === 1) {
        setSelectedConnection(result.connections[0]);
      }
    }
  };

  const loadJobs = async () => {
    const result = await lambdaService.listSQLJobs();
    if (result.success && result.jobs) {
      setJobs(result.jobs);
    }
  };

  // Load tables when connection changes
  useEffect(() => {
    if (selectedConnection) {
      loadTables();
      loadJobs();
    }
  }, [selectedConnection]);

  const loadTables = async () => {
    if (!selectedConnection) return;
    setLoadingTables(true);
    setTables([]);
    setSelectedTable(null);

    const result = await lambdaService.listDatabaseTables(selectedConnection.connection_id, {
      includeSize,
      includeRowCount,
    });
    setLoadingTables(false);

    if (result.success && result.tables) {
      setTables(result.tables);
    } else {
      showMessage('error', result.error || 'Failed to load tables');
    }
  };

  // Load schema when table changes
  useEffect(() => {
    if (selectedConnection && selectedTable) {
      loadTableSchema();
      loadPreview();
    }
  }, [selectedTable]);

  const loadTableSchema = async () => {
    if (!selectedConnection || !selectedTable) return;

    const result = await lambdaService.getTableSchema(selectedConnection.connection_id, selectedTable);

    if (result.success && result.columns) {
      setTableSchema(result.columns);
      setSelectedColumns(result.columns.map(c => c.name));
      setJobName(`Import ${selectedTable}`);

      // Auto-detect incremental column
      const dateCol = result.columns.find(c => c.is_datetime &&
        (c.name.toLowerCase().includes('created') ||
         c.name.toLowerCase().includes('updated') ||
         c.name.toLowerCase().includes('timestamp')));
      if (dateCol) {
        setIncrementalColumn(dateCol.name);
      }
    }
  };

  const loadPreview = async () => {
    if (!selectedConnection || !selectedTable) return;
    setLoadingPreview(true);

    const result = await lambdaService.previewTableData(
      selectedConnection.connection_id,
      selectedTable,
      { limit: 100 }
    );
    setLoadingPreview(false);

    if (result.success && result.rows) {
      setPreviewData(result.rows);
      setPreviewColumns(result.columns || []);
    }
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleCreateJob = async () => {
    if (!selectedConnection || !selectedTable) return;

    let query: string;
    if (importMode === 'sql') {
      query = customSQL;
    } else {
      const cols = selectedColumns.length === tableSchema.length
        ? '*'
        : selectedColumns.map(c => `"${c}"`).join(', ');

      // Generate database-specific query syntax
      const dbType = selectedConnection.type;
      if (dbType === 'sqlserver') {
        // SQL Server uses TOP and [brackets] for quoting
        const topClause = rowLimit ? `TOP ${rowLimit} ` : '';
        // Convert schema.table to [schema].[table]
        const tableParts = selectedTable.split('.');
        const quotedTable = tableParts.length === 2
          ? `[${tableParts[0]}].[${tableParts[1]}]`
          : `[${selectedTable}]`;
        query = `SELECT ${topClause}${cols} FROM ${quotedTable}`;
      } else {
        // PostgreSQL/MySQL use LIMIT and "quotes"
        query = `SELECT ${cols} FROM "${selectedTable}"`;
        if (rowLimit) {
          query += ` LIMIT ${rowLimit}`;
        }
      }
    }

    if (!query.trim()) {
      showMessage('error', 'Please provide a query');
      return;
    }

    setSaving(true);
    const result = await lambdaService.createSQLJob({
      name: jobName || `Import ${selectedTable}`,
      connectionId: selectedConnection.connection_id,
      query,
      schedule: schedule || null,
    });
    setSaving(false);

    if (result.success) {
      showMessage('success', 'Import job created successfully!');
      loadJobs();
      setViewMode('jobs');
    } else {
      showMessage('error', result.error || 'Failed to create job');
    }
  };

  const handleRunJob = async (jobId: string) => {
    setRunningJobId(jobId);
    toast.info('Starting job...');

    const result = await lambdaService.runSQLJob(jobId);
    setRunningJobId(null);

    if (result.success) {
      toast.success(`Job completed successfully! Rows: ${result.rows_ingested || 'processing...'}`);
      showMessage('success', `Job started! Rows: ${result.rows_ingested || 'processing...'}`);
      loadJobs();
    } else {
      toast.error(result.error || 'Failed to run job');
      showMessage('error', result.error || 'Failed to run job');
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    if (!window.confirm('Delete this import job?')) return;
    const result = await lambdaService.deleteSQLJob(jobId);
    if (result.success) {
      showMessage('success', 'Job deleted');
      loadJobs();
    } else {
      showMessage('error', result.error || 'Failed to delete job');
    }
  };

  const handleUpdateJob = async (jobId: string, updates: { schedule?: string; status?: string }) => {
    setSaving(true);
    const result = await lambdaService.updateSQLJob(jobId, updates);
    setSaving(false);
    if (result.success) {
      showMessage('success', 'Job updated');
      setEditingJob(null);
      loadJobs();
    } else {
      showMessage('error', result.error || 'Failed to update job');
    }
  };

  const loadJobHistory = async (jobId: string) => {
    setSelectedJobId(jobId);
    const result = await lambdaService.getSQLJobHistory(jobId);
    if (result.success && result.runs) {
      setJobHistory(result.runs);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const filteredTables = tables.filter(t =>
    (t.display_name || t.name).toLowerCase().includes(tableSearch.toLowerCase())
  );

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="sql-browser">
      {/* Top Navigation */}
      <div className="sql-nav">
        <div className="sql-nav-tabs">
          <button
            className={`sql-nav-tab ${viewMode === 'browser' ? 'active' : ''}`}
            onClick={() => setViewMode('browser')}
          >
            <Table2 className="w-5 h-5" />
            Table Browser
          </button>
          <button
            className={`sql-nav-tab ${viewMode === 'jobs' ? 'active' : ''}`}
            onClick={() => { setViewMode('jobs'); loadJobs(); }}
          >
            <Download className="w-5 h-5" />
            Import Jobs ({jobs.length})
          </button>
          <button
            className={`sql-nav-tab ${viewMode === 'history' ? 'active' : ''}`}
            onClick={() => setViewMode('history')}
          >
            <History className="w-5 h-5" />
            Run History
          </button>
          <button
            className={`sql-nav-tab ${viewMode === 'help' ? 'active' : ''}`}
            onClick={() => setViewMode('help')}
          >
            <HelpCircle className="w-5 h-5" />
            Setup Help
          </button>
        </div>

        {/* Connection Selector */}
        <div className="sql-connection-selector">
          <Database className="w-4 h-4" />
          <select
            value={selectedConnection?.connection_id || ''}
            onChange={(e) => {
              const conn = connections.find(c => c.connection_id === e.target.value);
              setSelectedConnection(conn || null);
            }}
          >
            <option value="">Select Connection...</option>
            {connections.map(conn => (
              <option key={conn.connection_id} value={conn.connection_id}>
                {DATABASE_TYPES.find(t => t.type === conn.type)?.icon} {conn.name}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={() => setShowConnectionForm(true)}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Message Banner */}
      {message && (
        <div className={`sql-message sql-message-${message.type}`}>
          {message.type === 'success' && <CheckCircle className="w-4 h-4" />}
          {message.type === 'error' && <AlertCircle className="w-4 h-4" />}
          {message.type === 'info' && <Info className="w-4 h-4" />}
          {message.text}
          <button onClick={() => setMessage(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Main Content */}
      <div className="sql-main">
        {viewMode === 'browser' && (
          <>
            {/* Sidebar - Table List */}
            <div className="sql-sidebar">
              <div className="sql-sidebar-header">
                <div className="sql-search">
                  <Search className="w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Filter tables..."
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={loadTables} disabled={loadingTables}>
                  <RefreshCw className={`w-4 h-4 ${loadingTables ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {/* Table detail options - off by default for large DB performance */}
              <div className="sql-table-options">
                <div className="sql-checkbox-label">
                  <Checkbox
                    id="include-row-count"
                    checked={includeRowCount}
                    onCheckedChange={(checked) => {
                      const isChecked = checked === true;
                      setIncludeRowCount(isChecked);
                      // Re-fetch tables with new options
                      if (selectedConnection) {
                        setLoadingTables(true);
                        setTables([]);
                        setSelectedTable(null);
                        lambdaService.listDatabaseTables(selectedConnection.connection_id, {
                          includeSize,
                          includeRowCount: isChecked,
                        }).then((result) => {
                          setLoadingTables(false);
                          if (result.success && result.tables) {
                            setTables(result.tables);
                          }
                        });
                      }
                    }}
                  />
                  <label htmlFor="include-row-count" className="cursor-pointer">Include Row Count</label>
                </div>
                <div className="sql-checkbox-label">
                  <Checkbox
                    id="include-table-size"
                    checked={includeSize}
                    onCheckedChange={(checked) => {
                      const isChecked = checked === true;
                      setIncludeSize(isChecked);
                      // Re-fetch tables with new options
                      if (selectedConnection) {
                        setLoadingTables(true);
                        setTables([]);
                        setSelectedTable(null);
                        lambdaService.listDatabaseTables(selectedConnection.connection_id, {
                          includeSize: isChecked,
                          includeRowCount,
                        }).then((result) => {
                          setLoadingTables(false);
                          if (result.success && result.tables) {
                            setTables(result.tables);
                          }
                        });
                      }
                    }}
                  />
                  <label htmlFor="include-table-size" className="cursor-pointer">Include Table Size</label>
                </div>
              </div>

              <div className="sql-table-list">
                {!selectedConnection ? (
                  <div className="sql-empty-state">
                    <Database className="w-8 h-8" />
                    <p>Select a connection to browse tables</p>
                  </div>
                ) : loadingTables ? (
                  <div className="sql-loading">
                    <RefreshCw className="w-6 h-6 animate-spin" />
                    <p>Loading tables...</p>
                  </div>
                ) : filteredTables.length === 0 ? (
                  <div className="sql-empty-state">
                    <Table2 className="w-8 h-8" />
                    <p>{tableSearch ? 'No tables match filter' : 'No tables found'}</p>
                  </div>
                ) : (
                  filteredTables.map(table => (
                    <button
                      key={table.name}
                      className={`sql-table-item ${selectedTable === table.name ? 'active' : ''}`}
                      onClick={() => setSelectedTable(table.name)}
                    >
                      <Table2 className="w-4 h-4" />
                      <div className="sql-table-info">
                        <span className="sql-table-name">{table.display_name || table.name}</span>
                        {(table.row_count !== undefined || table.size_bytes !== undefined) && (
                          <span className="sql-table-meta">
                            {table.row_count !== undefined && `${formatNumber(table.row_count)} rows`}
                            {table.row_count !== undefined && table.size_bytes !== undefined && ' · '}
                            {table.size_bytes !== undefined && formatBytes(table.size_bytes)}
                          </span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Content Area */}
            <div className="sql-content">
              {!selectedTable ? (
                <div className="sql-empty-state large">
                  <Table2 className="w-16 h-16" />
                  <h3>Select a Table</h3>
                  <p>Choose a table from the sidebar to preview data and set up imports</p>
                </div>
              ) : (
                <>
                  {/* Table Header */}
                  <div className="sql-content-header">
                    <div className="sql-table-title">
                      <Table2 className="w-5 h-5" />
                      <h2>{selectedTable}</h2>
                      <span className="sql-badge">{tableSchema.length} columns</span>
                    </div>
                    <div className="sql-mode-toggle">
                      <button
                        className={importMode === 'visual' ? 'active' : ''}
                        onClick={() => setImportMode('visual')}
                      >
                        <Columns className="w-4 h-4" />
                        Visual Builder
                      </button>
                      <button
                        className={importMode === 'sql' ? 'active' : ''}
                        onClick={() => setImportMode('sql')}
                      >
                        <Code className="w-4 h-4" />
                        Custom SQL
                      </button>
                    </div>
                  </div>

                  {/* Import Builder */}
                  <div className="sql-builder">
                    {importMode === 'visual' ? (
                      <div className="sql-visual-builder">
                        {/* Column Selection */}
                        <div className="sql-builder-section">
                          <h4><Columns className="w-4 h-4" /> Select Columns</h4>
                          <div className="sql-column-grid">
                            <label className="sql-column-all">
                              <Checkbox
                                checked={selectedColumns.length === tableSchema.length}
                                onCheckedChange={(checked) => {
                                  if (checked === true) {
                                    setSelectedColumns(tableSchema.map(c => c.name));
                                  } else {
                                    setSelectedColumns([]);
                                  }
                                }}
                              />
                              <span>Select All ({tableSchema.length})</span>
                            </label>
                            {tableSchema.map(col => (
                              <label key={col.name} className="sql-column-item">
                                <Checkbox
                                  checked={selectedColumns.includes(col.name)}
                                  onCheckedChange={(checked) => {
                                    if (checked === true) {
                                      setSelectedColumns([...selectedColumns, col.name]);
                                    } else {
                                      setSelectedColumns(selectedColumns.filter(c => c !== col.name));
                                    }
                                  }}
                                />
                                <span className="sql-col-name">{col.name}</span>
                                <span className="sql-col-type">{col.type}</span>
                                {col.is_primary_key && <Key className="w-3 h-3" />}
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Options Row */}
                        <div className="sql-builder-row">
                          <div className="sql-builder-section">
                            <h4><Filter className="w-4 h-4" /> Row Limit (First N Rows)</h4>
                            <div className="sql-limit-options">
                              <button
                                className={rowLimit === null ? 'active' : ''}
                                onClick={() => setRowLimit(null)}
                              >
                                All Rows
                              </button>
                              <button
                                className={rowLimit === 1000 ? 'active' : ''}
                                onClick={() => setRowLimit(1000)}
                              >
                                1,000
                              </button>
                              <button
                                className={rowLimit === 10000 ? 'active' : ''}
                                onClick={() => setRowLimit(10000)}
                              >
                                10,000
                              </button>
                              <button
                                className={rowLimit === 100000 ? 'active' : ''}
                                onClick={() => setRowLimit(100000)}
                              >
                                100,000
                              </button>
                              <input
                                type="number"
                                placeholder="Custom..."
                                value={rowLimit && ![1000, 10000, 100000].includes(rowLimit) ? rowLimit : ''}
                                onChange={(e) => setRowLimit(parseInt(e.target.value) || null)}
                              />
                            </div>
                          </div>

                          <div className="sql-builder-section">
                            <h4><Calendar className="w-4 h-4" /> Incremental Column</h4>
                            <p className="sql-hint">For scheduled imports, only fetch rows newer than last run</p>
                            <select
                              value={incrementalColumn || ''}
                              onChange={(e) => setIncrementalColumn(e.target.value || null)}
                            >
                              <option value="">None (full table each time)</option>
                              {tableSchema
                                .filter(c => c.is_datetime || c.is_numeric)
                                .map(col => (
                                  <option key={col.name} value={col.name}>
                                    {col.name} ({col.type})
                                  </option>
                                ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="sql-custom-builder">
                        <h4><Code className="w-4 h-4" /> Custom SQL Query</h4>
                        <textarea
                          value={customSQL}
                          onChange={(e) => setCustomSQL(e.target.value)}
                          placeholder={`SELECT * FROM "${selectedTable}" WHERE created_at > '2024-01-01'`}
                          rows={6}
                        />
                        <p className="sql-hint">
                          Write any SELECT query. Use standard SQL syntax for your database type.
                        </p>
                      </div>
                    )}

                    {/* Schedule & Save */}
                    <div className="sql-builder-footer">
                      <div className="sql-job-config">
                        <div className="sql-form-group">
                          <label>Job Name</label>
                          <input
                            type="text"
                            value={jobName}
                            onChange={(e) => setJobName(e.target.value)}
                            placeholder="My Import Job"
                          />
                        </div>
                        <div className="sql-form-group">
                          <label><Clock className="w-4 h-4" /> Schedule</label>
                          <select value={schedule} onChange={(e) => setSchedule(e.target.value)}>
                            {SCHEDULE_PRESETS.map(preset => (
                              <option key={preset.value} value={preset.value}>
                                {preset.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="sql-builder-actions">
                        <Button variant="outline" onClick={loadPreview} disabled={loadingPreview} className="flex items-center gap-2">
                          <Eye className="w-5 h-5" />
                          {loadingPreview ? 'Loading...' : 'Preview Data'}
                        </Button>
                        <Button onClick={handleCreateJob} disabled={saving || selectedColumns.length === 0} className="flex items-center gap-2">
                          <Download className="w-5 h-5" />
                          {saving ? 'Creating...' : 'Create Import Job'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Data Preview */}
                  <div className="sql-preview">
                    <h4><Eye className="w-4 h-4" /> Data Preview (First 100 rows)</h4>
                    {loadingPreview ? (
                      <div className="sql-loading">
                        <RefreshCw className="w-6 h-6 animate-spin" />
                      </div>
                    ) : previewData.length === 0 ? (
                      <div className="sql-empty-state">
                        <p>Click "Preview Data" to see sample rows</p>
                      </div>
                    ) : (
                      <div className="sql-data-table">
                        <table>
                          <thead>
                            <tr>
                              {previewColumns.map(col => (
                                <th key={col}>{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.slice(0, 20).map((row, i) => (
                              <tr key={i}>
                                {previewColumns.map(col => (
                                  <td key={col}>
                                    {row[col] === null ? <span className="null">NULL</span> : String(row[col]).substring(0, 100)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {previewData.length > 20 && (
                          <p className="sql-hint">Showing 20 of {previewData.length} preview rows</p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {viewMode === 'jobs' && (
          <div className="sql-jobs-view">
            <div className="sql-jobs-header">
              <h2><Download className="w-5 h-5" /> Import Jobs</h2>
              <Button variant="outline" onClick={loadJobs} className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Refresh
              </Button>
            </div>

            {jobs.length === 0 ? (
              <div className="sql-empty-state large">
                <Download className="w-16 h-16" />
                <h3>No Import Jobs</h3>
                <p>Create a job from the Table Browser to start importing data</p>
                <Button onClick={() => setViewMode('browser')} className="flex items-center gap-2">
                  <Table2 className="w-5 h-5" />
                  Go to Table Browser
                </Button>
              </div>
            ) : (
              <div className="sql-jobs-list">
                {jobs.map(job => (
                  <div key={job.job_id} className="sql-job-card">
                    <div className="sql-job-header">
                      <div className="sql-job-title">
                        <h3>{job.name}</h3>
                        <span className={`sql-status sql-status-${job.status}`}>
                          {job.status}
                        </span>
                        {job.lastRunStatus && (
                          <span className={`sql-status sql-status-${job.lastRunStatus}`}>
                            Last: {job.lastRunStatus}
                          </span>
                        )}
                      </div>
                      <div className="sql-job-actions">
                        <Button
                          size="sm"
                          onClick={() => handleRunJob(job.job_id)}
                          className="flex items-center gap-2"
                          disabled={runningJobId === job.job_id}
                        >
                          {runningJobId === job.job_id ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Running...
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4" />
                              Run Now
                            </>
                          )}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingJob(job)} className="flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setViewMode('history'); loadJobHistory(job.job_id); }} className="flex items-center gap-2">
                          <History className="w-4 h-4" />
                          History
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteJob(job.job_id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="sql-job-meta">
                      <span><Database className="w-4 h-4" /> {job.connectionName}</span>
                      <span><Clock className="w-4 h-4" /> {job.schedule || 'Manual'}</span>
                      {job.lastRunAt && (
                        <span><Calendar className="w-4 h-4" /> Last: {new Date(job.lastRunAt).toLocaleString()}</span>
                      )}
                    </div>
                    <div className="sql-job-query">
                      <code>{job.query ? `${job.query.substring(0, 200)}${job.query.length > 200 ? '...' : ''}` : 'No query defined'}</code>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {viewMode === 'history' && (
          <div className="sql-history-view">
            <div className="sql-history-header">
              <h2><History className="w-5 h-5" /> Run History</h2>
              <select
                value={selectedJobId || ''}
                onChange={(e) => e.target.value && loadJobHistory(e.target.value)}
              >
                <option value="">Select a job...</option>
                {jobs.map(job => (
                  <option key={job.job_id} value={job.job_id}>{job.name}</option>
                ))}
              </select>
            </div>

            {jobHistory.length === 0 ? (
              <div className="sql-empty-state large">
                <History className="w-16 h-16" />
                <h3>No Run History</h3>
                <p>Select a job to view its run history</p>
              </div>
            ) : (
              <div className="sql-history-list">
                {jobHistory.map(run => (
                  <div key={run.runId} className={`sql-run-card sql-run-${run.status}`}>
                    <div className="sql-run-status">
                      {run.status === 'success' && <CheckCircle className="w-5 h-5" />}
                      {run.status === 'failed' && <AlertCircle className="w-5 h-5" />}
                      {run.status === 'running' && <RefreshCw className="w-5 h-5 animate-spin" />}
                    </div>
                    <div className="sql-run-info">
                      <div className="sql-run-time">{new Date(run.startedAt).toLocaleString()}</div>
                      <div className="sql-run-stats">
                        {run.rowsIngested !== undefined && <span>{run.rowsIngested.toLocaleString()} rows</span>}
                        {run.fileSizeBytes !== undefined && <span>{formatBytes(run.fileSizeBytes)}</span>}
                        {run.durationSeconds !== undefined && <span>{run.durationSeconds}s</span>}
                      </div>
                      {run.errorMessage && (
                        <div className="sql-run-error">{run.errorMessage}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {viewMode === 'help' && (
          <div className="sql-help-view">
            <h2><Shield className="w-5 h-5" /> Secure Database Setup</h2>

            <div className="sql-help-cards">
              <Card>
                <CardHeader>
                  <CardTitle className="sql-card-title"><Lock className="w-5 h-5" /> Option 1: Public Endpoint</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>For databases with public endpoints (RDS, Cloud SQL, Azure SQL):</p>
                  <ol>
                    <li>Ensure your database has a public endpoint enabled</li>
                    <li>Create a dedicated database user with read-only access</li>
                    <li>Use strong credentials and SSL encryption</li>
                  </ol>
                  <p className="sql-hint">For production databases requiring IP whitelisting, use SSH Tunnel (Option 3) or contact us for VPC peering.</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="sql-card-title"><Server className="w-5 h-5" /> Option 2: VPC Peering (Most Secure)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>For databases in a private VPC, we can establish VPC peering:</p>
                  <ol>
                    <li>Contact us with your VPC ID and CIDR block</li>
                    <li>We'll send you a peering request</li>
                    <li>Accept the request and update route tables</li>
                    <li>Enable the VPC option when creating connections</li>
                  </ol>
                  <p className="sql-hint">Best for production databases with sensitive data</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="sql-card-title"><Terminal className="w-5 h-5" /> Option 3: SSH Tunnel (Recommended)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>Connect through an SSH bastion host for databases without public access:</p>
                  <ol>
                    <li>Enable "Use SSH Tunnel" when creating a connection</li>
                    <li>Enter your bastion host address and SSH username</li>
                    <li>Paste your SSH private key (encrypted and stored securely)</li>
                    <li>Enter your database host as seen from the bastion</li>
                  </ol>
                  <p className="sql-hint">Best for private databases behind a bastion/jump host. No public endpoint required.</p>
                  <a
                    href="https://docs.aws.amazon.com/mwaa/latest/userguide/tutorials-private-network-bastion.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sql-doc-link"
                  >
                    <ExternalLink className="w-4 h-4" />
                    AWS Guide: Setting up a Bastion Host
                  </a>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="sql-card-title"><Key className="w-5 h-5" /> Credential Security</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>Your database credentials are protected:</p>
                  <ul className="sql-security-list">
                    <li><CheckCircle className="w-4 h-4" /> Encrypted at rest using AWS KMS</li>
                    <li><CheckCircle className="w-4 h-4" /> Stored in AWS Secrets Manager</li>
                    <li><CheckCircle className="w-4 h-4" /> Never logged or exposed in plaintext</li>
                    <li><CheckCircle className="w-4 h-4" /> Only accessible by your user account</li>
                  </ul>
                  <p className="sql-hint">We recommend using a read-only database user with limited table access</p>
                </CardContent>
              </Card>
            </div>

            <div className="sql-help-tips">
              <h3>Best Practices</h3>
              <ul>
                <li><strong>Create a read-only user:</strong> Only grant SELECT permissions to the BigSplice user</li>
                <li><strong>Use SSL:</strong> Enable SSL/TLS encryption for database connections</li>
                <li><strong>Limit access:</strong> Only allow access to specific tables/schemas needed</li>
                <li><strong>Monitor queries:</strong> Enable query logging to track BigSplice's access</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Connection Form Modal */}
      {showConnectionForm && (
        <ConnectionFormModal
          onClose={() => setShowConnectionForm(false)}
          onSuccess={() => {
            setShowConnectionForm(false);
            loadConnections();
          }}
        />
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <EditJobModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSave={(updates) => handleUpdateJob(editingJob.job_id, updates)}
          saving={saving}
        />
      )}

      {/* Bottom-right toast for import behavior help */}
      {schedule && selectedTable && (
        <div className={`sql-import-toast ${incrementalColumn ? 'incremental' : 'full-refresh'} ${toastCollapsed ? 'collapsed' : ''}`}>
          <div className="toast-header" onClick={() => toastCollapsed && setToastCollapsed(false)}>
            <Info className="w-4 h-4" />
            <span>{incrementalColumn ? 'Incremental Import' : 'Full Table Import'}</span>
            <button
              className="toast-collapse-btn"
              onClick={(e) => { e.stopPropagation(); setToastCollapsed(!toastCollapsed); }}
              title={toastCollapsed ? 'Expand' : 'Collapse'}
            >
              <ChevronDown className={`w-4 h-4 ${toastCollapsed ? 'rotated' : ''}`} />
            </button>
          </div>
          {!toastCollapsed && (
            <>
              {/* Warning when using row limit with incremental imports */}
              {rowLimit && incrementalColumn && (
                <div className="toast-limit-warning">
                  <AlertCircle className="w-4 h-4" />
                  <span>
                    Row limit may cause data loss if &gt;{rowLimit.toLocaleString()} new rows between runs.
                  </span>
                </div>
              )}
              {incrementalColumn ? (
                <>
                  <p>
                    Each scheduled run will fetch only <strong>new rows</strong> since the last import
                    (where "{incrementalColumn}" &gt; last run time). All parts are kept in a folder.
                  </p>
                  <div className="toast-example">
                    <strong>Example:</strong> Table has 50,000 total rows
                    <ul>
                      <li>Run 1: Fetches 50,000 rows → part_1</li>
                      <li>Run 2: Fetches 127 new rows → part_2</li>
                      <li>Run 3: Fetches 89 new rows → part_3</li>
                    </ul>
                    <p className="toast-result">
                      Result: <strong>1 folder</strong> with 50,216 total rows across 3 parts
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p>
                    Each scheduled run will fetch <strong>ALL rows</strong> from "{selectedTable}" and
                    <strong> replace</strong> the previous file.
                  </p>
                  <div className="toast-example">
                    <strong>Example:</strong>
                    <ul>
                      <li>Run 1 (6:00 AM): 10,000 rows → sql_import</li>
                      <li>Run 2 (7:00 AM): 10,050 rows → sql_import (replaced)</li>
                    </ul>
                    <p className="toast-result">
                      Result: <strong>1 file</strong> with 10,050 rows
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <style>{`
        .sql-browser {
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: none;
          height: calc(100vh - 240px);
          min-height: 700px;
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          overflow: hidden;
        }

        /* Navigation */
        .sql-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: hsl(var(--muted));
          border-bottom: 1px solid hsl(var(--border));
          flex-shrink: 0;
          min-height: 56px;
        }
        .sql-nav-tabs {
          display: flex;
          gap: 4px;
          align-items: center;
        }
        .sql-nav-tab {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 14px;
          height: 36px;
          border: none;
          background: transparent;
          color: hsl(var(--muted-foreground));
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .sql-nav-tab:hover {
          background: hsl(var(--accent));
          color: hsl(var(--accent-foreground));
        }
        .sql-nav-tab.active {
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
        }
        .sql-connection-selector {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .sql-connection-selector select {
          padding: 8px 12px;
          height: 36px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          font-size: 13px;
          min-width: 200px;
        }
        .sql-connection-selector button {
          height: 36px;
          width: 36px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Message Banner */
        .sql-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          font-size: 13px;
        }
        .sql-message button {
          margin-left: auto;
          background: none;
          border: none;
          cursor: pointer;
          opacity: 0.7;
        }
        .sql-message-success {
          background: hsl(142 76% 36% / 0.1);
          color: hsl(142 76% 36%);
        }
        .sql-message-error {
          background: hsl(var(--destructive) / 0.1);
          color: hsl(var(--destructive));
        }
        .sql-message-info {
          background: hsl(var(--primary) / 0.1);
          color: hsl(var(--primary));
        }

        /* Main Layout */
        .sql-main {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        /* Sidebar */
        .sql-sidebar {
          width: 280px;
          min-width: 280px;
          border-right: 1px solid hsl(var(--border));
          display: flex;
          flex-direction: column;
          background: hsl(var(--card));
          flex-shrink: 0;
        }
        .sql-sidebar-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          border-bottom: 1px solid hsl(var(--border));
          flex-shrink: 0;
        }
        .sql-search {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 10px;
          height: 34px;
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
        }
        .sql-search input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 13px;
          color: hsl(var(--foreground));
          outline: none;
          height: 100%;
        }
        .sql-search svg {
          color: hsl(var(--muted-foreground));
          flex-shrink: 0;
        }
        .sql-sidebar-header > button {
          height: 34px;
          width: 34px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .sql-table-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 12px;
          border-bottom: 1px solid hsl(var(--border));
          background: hsl(var(--muted) / 0.3);
          flex-shrink: 0;
        }
        .sql-checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          user-select: none;
        }
        .sql-checkbox-label label {
          color: hsl(var(--foreground));
          font-weight: 500;
        }
        .sql-table-list {
          flex: 1;
          overflow-y: auto;
          padding: 8px;
        }
        .sql-table-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 12px;
          min-height: 48px;
          border: none;
          background: transparent;
          color: hsl(var(--foreground));
          border-radius: 6px;
          cursor: pointer;
          text-align: left;
          transition: all 0.15s;
          margin-bottom: 4px;
        }
        .sql-table-item > svg {
          flex-shrink: 0;
        }
        .sql-table-item:hover {
          background: hsl(var(--accent));
        }
        .sql-table-item.active {
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
        }
        .sql-table-item.active .sql-table-meta {
          color: hsl(var(--primary-foreground) / 0.8);
        }
        .sql-table-info {
          flex: 1;
          min-width: 0;
        }
        .sql-table-name {
          display: block;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .sql-table-meta {
          display: block;
          font-size: 11px;
          color: hsl(var(--muted-foreground));
          margin-top: 2px;
        }

        /* Content Area */
        .sql-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: hsl(var(--background));
        }
        .sql-content-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid hsl(var(--border));
        }
        .sql-table-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .sql-table-title h2 {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
        }
        .sql-badge {
          padding: 3px 8px;
          background: hsl(var(--muted));
          color: hsl(var(--muted-foreground));
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }
        .sql-mode-toggle {
          display: flex;
          gap: 4px;
          padding: 4px;
          background: hsl(var(--muted));
          border-radius: 8px;
          height: 40px;
          align-items: center;
        }
        .sql-mode-toggle button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 0 14px;
          height: 32px;
          border: none;
          background: transparent;
          color: hsl(var(--muted-foreground));
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }
        .sql-mode-toggle button.active {
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        /* Builder */
        .sql-builder {
          padding: 20px;
          border-bottom: 1px solid hsl(var(--border));
          flex: 0 0 auto;
        }
        .sql-builder-section {
          margin-bottom: 20px;
        }
        .sql-builder-section h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          margin: 0 0 10px 0;
          color: hsl(var(--foreground));
        }
        .sql-builder-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .sql-column-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 6px;
          max-height: 200px;
          overflow-y: auto;
          padding: 12px;
          background: hsl(var(--muted) / 0.5);
          border-radius: 8px;
        }
        .sql-column-all {
          grid-column: 1 / -1;
          padding-bottom: 8px;
          margin-bottom: 8px;
          border-bottom: 1px solid hsl(var(--border));
        }
        .sql-column-item, .sql-column-all {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          cursor: pointer;
        }
        .sql-col-name {
          flex: 1;
          font-weight: 500;
        }
        .sql-col-type {
          color: hsl(var(--muted-foreground));
          font-size: 11px;
        }
        .sql-limit-options {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: center;
        }
        .sql-limit-options button, .sql-limit-options input {
          padding: 0 12px;
          height: 34px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sql-limit-options button.active {
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
          border-color: hsl(var(--primary));
        }
        .sql-limit-options input {
          width: 100px;
          text-align: center;
        }
        .sql-hint {
          font-size: 11px;
          color: hsl(var(--muted-foreground));
          margin: 6px 0 0 0;
        }
        .sql-doc-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: hsl(var(--primary));
          text-decoration: none;
          margin-top: 10px;
          padding: 6px 10px;
          background: hsl(var(--primary) / 0.1);
          border-radius: 6px;
          transition: background 0.2s;
        }
        .sql-doc-link:hover {
          background: hsl(var(--primary) / 0.2);
          text-decoration: underline;
        }
        .sql-doc-link svg {
          flex-shrink: 0;
        }
        .sql-builder-section select {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          font-size: 13px;
        }
        .sql-custom-builder textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          background: hsl(220 13% 14%);
          color: hsl(220 13% 91%);
          font-family: monospace;
          font-size: 13px;
          resize: vertical;
        }
        .sql-builder-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid hsl(var(--border));
          gap: 16px;
        }
        .sql-job-config {
          display: flex;
          gap: 16px;
          align-items: flex-end;
        }
        .sql-builder-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .sql-builder-actions button {
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sql-form-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .sql-form-group label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 500;
          color: hsl(var(--foreground));
        }
        .sql-form-group input, .sql-form-group select {
          padding: 8px 12px;
          height: 38px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          font-size: 13px;
          min-width: 180px;
        }
        .sql-builder-actions {
          display: flex;
          gap: 8px;
        }

        /* Preview */
        .sql-preview {
          flex: 1 1 0;
          min-height: 200px;
          padding: 16px 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .sql-preview h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          margin: 0 0 12px 0;
        }
        .sql-data-table {
          flex: 1;
          overflow: auto;
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
        }
        .sql-data-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        .sql-data-table th {
          position: sticky;
          top: 0;
          background: hsl(var(--muted));
          padding: 10px 12px;
          text-align: left;
          font-weight: 600;
          border-bottom: 1px solid hsl(var(--border));
          white-space: nowrap;
        }
        .sql-data-table td {
          padding: 8px 12px;
          border-bottom: 1px solid hsl(var(--border) / 0.5);
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .sql-data-table tr:hover td {
          background: hsl(var(--muted) / 0.5);
        }
        .sql-data-table .null {
          color: hsl(var(--muted-foreground));
          font-style: italic;
        }

        /* Empty/Loading States */
        .sql-empty-state, .sql-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          color: hsl(var(--muted-foreground));
          text-align: center;
        }
        .sql-empty-state.large {
          flex: 1;
        }
        .sql-empty-state svg, .sql-loading svg {
          margin-bottom: 12px;
          opacity: 0.5;
        }
        .sql-empty-state h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 8px 0;
          color: hsl(var(--foreground));
        }
        .sql-empty-state p {
          margin: 0 0 16px 0;
          font-size: 13px;
        }

        /* Jobs View */
        .sql-jobs-view, .sql-history-view, .sql-help-view {
          flex: 1;
          padding: 24px;
          overflow-y: auto;
        }
        .sql-jobs-header, .sql-history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .sql-jobs-header h2, .sql-history-header h2 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 20px;
          font-weight: 600;
          margin: 0;
        }
        .sql-history-header select {
          padding: 8px 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          font-size: 13px;
          min-width: 200px;
        }
        .sql-jobs-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .sql-job-card {
          padding: 16px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
        }
        .sql-job-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }
        .sql-job-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .sql-job-title h3 {
          font-size: 15px;
          font-weight: 600;
          margin: 0;
        }
        .sql-status {
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }
        .sql-status-active, .sql-status-success {
          background: hsl(142 76% 36% / 0.1);
          color: hsl(142 76% 36%);
        }
        .sql-status-paused, .sql-status-running {
          background: hsl(var(--primary) / 0.1);
          color: hsl(var(--primary));
        }
        .sql-status-failed {
          background: hsl(var(--destructive) / 0.1);
          color: hsl(var(--destructive));
        }
        .sql-job-actions {
          display: flex;
          gap: 8px;
        }
        .sql-job-meta {
          display: flex;
          gap: 16px;
          font-size: 12px;
          color: hsl(var(--muted-foreground));
          margin-bottom: 12px;
        }
        .sql-job-meta span {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .sql-job-query {
          padding: 10px;
          background: hsl(var(--muted) / 0.5);
          border-radius: 6px;
        }
        .sql-job-query code {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
        }

        /* History View */
        .sql-history-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .sql-run-card {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 16px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
        }
        .sql-run-success .sql-run-status { color: hsl(142 76% 36%); }
        .sql-run-failed .sql-run-status { color: hsl(var(--destructive)); }
        .sql-run-running .sql-run-status { color: hsl(var(--primary)); }
        .sql-run-info {
          flex: 1;
        }
        .sql-run-time {
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 4px;
        }
        .sql-run-stats {
          display: flex;
          gap: 12px;
          font-size: 12px;
          color: hsl(var(--muted-foreground));
        }
        .sql-run-error {
          margin-top: 8px;
          padding: 8px;
          background: hsl(var(--destructive) / 0.1);
          color: hsl(var(--destructive));
          border-radius: 4px;
          font-size: 12px;
        }

        /* Help View */
        .sql-help-view h2 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 20px 0;
        }
        .sql-help-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        .sql-help-cards p {
          margin: 0 0 12px 0;
          font-size: 13px;
          color: hsl(var(--muted-foreground));
        }
        .sql-help-cards ol, .sql-help-cards ul {
          margin: 0;
          padding-left: 24px;
          font-size: 13px;
          color: hsl(var(--muted-foreground));
        }
        .sql-help-cards ol {
          list-style-type: decimal;
        }
        .sql-help-cards ul {
          list-style-type: disc;
        }
        .sql-help-cards li {
          margin-bottom: 6px;
          padding-left: 4px;
        }
        .sql-help-cards li::marker {
          color: hsl(var(--foreground));
        }
        .sql-card-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 15px;
        }
        .sql-card-title svg {
          flex-shrink: 0;
        }
        .sql-security-list {
          list-style: none;
          padding-left: 0 !important;
        }
        .sql-security-list li {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
          color: hsl(var(--foreground));
        }
        .sql-security-list li svg {
          flex-shrink: 0;
          color: hsl(142 76% 36%);
        }
        .sql-code-block {
          padding: 12px;
          background: hsl(220 13% 14%);
          color: hsl(220 13% 91%);
          border-radius: 6px;
          margin: 12px 0;
        }
        .sql-code-block code {
          font-size: 12px;
          line-height: 1.5;
        }
        .sql-help-tips {
          padding: 20px;
          background: hsl(var(--muted) / 0.5);
          border-radius: 8px;
        }
        .sql-help-tips h3 {
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 12px 0;
        }
        .sql-help-tips ul {
          margin: 0;
          padding-left: 24px;
          font-size: 13px;
          list-style-type: disc;
        }
        .sql-help-tips li {
          margin-bottom: 8px;
          padding-left: 4px;
        }
        .sql-help-tips li::marker {
          color: hsl(var(--foreground));
        }

        /* Modal Styles (shared) */
        .sql-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .sql-modal {
          width: 100%;
          max-width: 500px;
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
        }
        .sql-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid hsl(var(--border));
        }
        .sql-modal-header h3 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }
        .sql-modal-header button {
          background: none;
          border: none;
          cursor: pointer;
          color: hsl(var(--muted-foreground));
          padding: 4px;
          border-radius: 4px;
        }
        .sql-modal-header button:hover {
          background: hsl(var(--muted));
        }
        .sql-modal-body {
          padding: 20px;
        }
        .sql-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .sql-form-grid .full {
          grid-column: 1 / -1;
        }
        .sql-form-group label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 6px;
          color: hsl(var(--foreground));
        }
        .sql-form-group input, .sql-form-group select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          font-size: 13px;
        }
        .sql-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 16px 20px;
          border-top: 1px solid hsl(var(--border));
          background: hsl(var(--muted) / 0.3);
        }

        /* Bottom-right Toast for Import Help */
        .sql-import-toast {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 340px;
          padding: 16px;
          border-radius: 12px;
          font-size: 13px;
          line-height: 1.6;
          z-index: 1000;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease-out;
          opacity: 0.9;
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .sql-import-toast.incremental {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(139, 92, 246, 0.05));
          border: 1px solid rgba(139, 92, 246, 0.3);
        }
        .sql-import-toast.full-refresh {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05));
          border: 1px solid rgba(59, 130, 246, 0.3);
        }
        .sql-import-toast .toast-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 10px;
        }
        .sql-import-toast.collapsed .toast-header {
          margin-bottom: 0;
          cursor: pointer;
        }
        .sql-import-toast.incremental .toast-header {
          color: #a78bfa;
        }
        .sql-import-toast.full-refresh .toast-header {
          color: #60a5fa;
        }
        .sql-import-toast .toast-collapse-btn {
          margin-left: auto;
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: hsl(var(--muted-foreground));
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .sql-import-toast .toast-collapse-btn:hover {
          background: hsl(var(--muted) / 0.5);
          color: hsl(var(--foreground));
        }
        .sql-import-toast .toast-collapse-btn svg {
          transition: transform 0.2s;
        }
        .sql-import-toast .toast-collapse-btn svg.rotated {
          transform: rotate(180deg);
        }
        .sql-import-toast p {
          margin: 8px 0;
          color: hsl(var(--muted-foreground));
        }
        .sql-import-toast .toast-example {
          margin: 12px 0 0 0;
          padding: 10px;
          background: hsl(var(--muted) / 0.4);
          border-radius: 6px;
        }
        .sql-import-toast .toast-example strong {
          color: hsl(var(--foreground));
        }
        .sql-import-toast .toast-example ul {
          margin: 6px 0 6px 16px;
          padding: 0;
        }
        .sql-import-toast .toast-example li {
          margin: 3px 0;
          font-family: ui-monospace, monospace;
          font-size: 11px;
          color: hsl(var(--muted-foreground));
        }
        .sql-import-toast .toast-result {
          margin: 8px 0 0 0;
          padding-top: 8px;
          border-top: 1px dashed hsl(var(--border));
          font-size: 12px;
        }

        /* Row limit warning inside toast */
        .toast-limit-warning {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          background: rgba(245, 158, 11, 0.2);
          border: 1px solid rgba(245, 158, 11, 0.4);
          border-radius: 6px;
          margin-bottom: 10px;
        }
        .toast-limit-warning svg {
          color: #f59e0b;
          flex-shrink: 0;
        }
        .toast-limit-warning span {
          font-size: 12px;
          color: #fbbf24;
          line-height: 1.4;
        }
      `}</style>
    </div>
  );
};

// ============================================================
// Connection Form Modal
// ============================================================

interface ConnectionFormModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const ConnectionFormModal: React.FC<ConnectionFormModalProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<SQLConnectionInput>({
    name: '',
    type: 'postgresql',
    host: '',
    port: 5432,
    database: '',
    username: '',
    password: '',
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  const handleTypeChange = (type: DatabaseType) => {
    const defaultPort = DATABASE_TYPES.find(t => t.type === type)?.defaultPort || 5432;
    setFormData(prev => ({ ...prev, type, port: defaultPort }));
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setTestResult(null);

    // Debug logging to help diagnose SSH tunnel issues
    console.log('=== SSH TUNNEL TEST DEBUG ===');
    console.log('DB host:', formData.host);
    console.log('DB port:', formData.port);
    console.log('useSshTunnel:', formData.useSshTunnel);
    if (formData.useSshTunnel && formData.sshConfig) {
      console.log('sshConfig.host:', formData.sshConfig.host);
      console.log('sshConfig.port:', formData.sshConfig.port);
      console.log('sshConfig.username:', formData.sshConfig.username);
      console.log('sshConfig.privateKey length:', formData.sshConfig.privateKey?.length || 0);
      console.log('privateKey starts with:', formData.sshConfig.privateKey?.substring(0, 40) || 'EMPTY');

      if (!formData.sshConfig.privateKey || formData.sshConfig.privateKey.length === 0) {
        toast.error('SSH private key is empty! Please import a .pem file or paste your key.');
        setTesting(false);
        setError('SSH private key is required for SSH tunnel connections');
        return;
      }
    }
    console.log('=============================');

    const result = await lambdaService.testSQLConnection(formData);
    setTesting(false);
    if (result.success) {
      setTestResult(`Connected! Server: ${result.serverVersion?.substring(0, 50)}...`);
    } else {
      setError(result.error || result.details || 'Connection failed');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    // Debug logging to help diagnose SSH tunnel issues
    console.log('=== SSH TUNNEL DEBUG INFO ===');
    console.log('useSshTunnel:', formData.useSshTunnel);
    if (formData.useSshTunnel && formData.sshConfig) {
      console.log('sshConfig.host:', formData.sshConfig.host);
      console.log('sshConfig.port:', formData.sshConfig.port);
      console.log('sshConfig.username:', formData.sshConfig.username);
      console.log('sshConfig.privateKey length:', formData.sshConfig.privateKey?.length || 0);
      console.log('privateKey starts with:', formData.sshConfig.privateKey?.substring(0, 40) || 'EMPTY');

      if (!formData.sshConfig.privateKey || formData.sshConfig.privateKey.length === 0) {
        toast.error('SSH private key is empty! Please import a .pem file or paste your key.');
        setSaving(false);
        setError('SSH private key is required for SSH tunnel connections');
        return;
      }
    }
    console.log('==============================');

    const result = await lambdaService.createSQLConnection(formData);
    setSaving(false);
    if (result.success) {
      onSuccess();
    } else {
      setError(result.error || 'Failed to create connection');
    }
  };

  return (
    <div className="sql-modal-overlay">
      <div className="sql-modal">
        <div className="sql-modal-header">
          <h3><Database className="w-5 h-5" /> New Database Connection</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="sql-modal-body">
          {error && <div className="sql-message sql-message-error"><AlertCircle className="w-4 h-4" />{error}</div>}
          {testResult && <div className="sql-message sql-message-success"><CheckCircle className="w-4 h-4" />{testResult}</div>}

          <div className="sql-form-grid">
            <div className="sql-form-group full">
              <label>Connection Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, name: val }));
                }}
                placeholder="My Production Database"
              />
            </div>

            <div className="sql-form-group">
              <label>Database Type</label>
              <select value={formData.type} onChange={(e) => handleTypeChange(e.target.value as DatabaseType)}>
                {DATABASE_TYPES.map(t => (
                  <option key={t.type} value={t.type}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>

            <div className="sql-form-group">
              <label>Port</label>
              <input
                type="number"
                value={formData.port}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 5432;
                  setFormData(prev => ({ ...prev, port: val }));
                }}
              />
            </div>

            <div className="sql-form-group full">
              <label>Host</label>
              <input
                type="text"
                value={formData.host}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, host: val }));
                }}
                placeholder="db.example.com or 10.0.0.1"
              />
            </div>

            <div className="sql-form-group full">
              <label>Database Name</label>
              <input
                type="text"
                value={formData.database}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, database: val }));
                }}
                placeholder="mydb"
              />
            </div>

            <div className="sql-form-group">
              <label>Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, username: val }));
                }}
                placeholder="dbuser"
              />
            </div>

            <div className="sql-form-group">
              <label>Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, password: val }));
                }}
                placeholder="••••••••"
              />
            </div>

            {/* SSH Tunnel Section */}
            <div className="sql-form-group full">
              <label className="sql-toggle-label">
                <Checkbox
                  checked={formData.useSshTunnel || false}
                  onCheckedChange={(checked) => {
                    const isChecked = checked === true;
                    setFormData(prev => ({
                      ...prev,
                      useSshTunnel: isChecked,
                      sshConfig: isChecked ? { host: '', port: 22, username: '', privateKey: '' } : undefined
                    }));
                  }}
                />
                <Terminal className="w-4 h-4" />
                Use SSH Tunnel (for private databases)
              </label>
            </div>

            {formData.useSshTunnel && (
              <>
                <div className="sql-form-group">
                  <label>SSH Host (Bastion)</label>
                  <input
                    type="text"
                    value={formData.sshConfig?.host || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        sshConfig: { ...prev.sshConfig!, host: val }
                      }));
                    }}
                    placeholder="bastion.example.com"
                  />
                </div>

                <div className="sql-form-group">
                  <label>SSH Port</label>
                  <input
                    type="number"
                    value={formData.sshConfig?.port || 22}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 22;
                      setFormData(prev => ({
                        ...prev,
                        sshConfig: { ...prev.sshConfig!, port: val }
                      }));
                    }}
                  />
                </div>

                <div className="sql-form-group full">
                  <label>SSH Username</label>
                  <input
                    type="text"
                    value={formData.sshConfig?.username || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        sshConfig: { ...prev.sshConfig!, username: val }
                      }));
                    }}
                    placeholder="ec2-user"
                  />
                </div>

                <div className="sql-form-group full">
                  <div className="sql-label-row">
                    <label>SSH Private Key (PEM format)</label>
                    <label className="sql-file-import-btn">
                      <input
                        type="file"
                        accept=".pem,.key,.txt"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              const content = event.target?.result as string;
                              // Use functional update to avoid stale closure
                              setFormData(prev => ({
                                ...prev,
                                sshConfig: { ...prev.sshConfig!, privateKey: content }
                              }));
                              toast.success(`Loaded key from ${file.name}`);
                            };
                            reader.readAsText(file);
                          }
                          e.target.value = ''; // Reset to allow re-selecting same file
                        }}
                      />
                      <Download className="w-3 h-3" />
                      Import .pem file
                    </label>
                  </div>
                  <textarea
                    value={formData.sshConfig?.privateKey || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        sshConfig: { ...prev.sshConfig!, privateKey: val }
                      }));
                    }}
                    placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
                    rows={4}
                    className="sql-textarea-key"
                  />
                  <p className="sql-hint">Import a .pem file or paste your SSH private key. It will be encrypted and stored securely.</p>
                  <a
                    href="https://docs.aws.amazon.com/mwaa/latest/userguide/tutorials-private-network-bastion.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sql-doc-link"
                  >
                    <ExternalLink className="w-4 h-4" />
                    AWS Guide: Setting up a Bastion Host
                  </a>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="sql-modal-footer">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={testing || !formData.host || !formData.database}
          >
            {testing ? <><RefreshCw className="w-4 h-4 animate-spin" /> Testing...</> : 'Test Connection'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !formData.name || !formData.host || !formData.database}
          >
            {saving ? 'Saving...' : 'Save Connection'}
          </Button>
        </div>
      </div>

      <style>{`
        .sql-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          padding: 20px;
        }
        .sql-modal {
          width: 100%;
          max-width: 600px;
          max-height: calc(100vh - 40px);
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
        }
        .sql-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid hsl(var(--border));
          flex-shrink: 0;
        }
        .sql-modal-header h3 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }
        .sql-modal-header button {
          background: none;
          border: none;
          cursor: pointer;
          color: hsl(var(--muted-foreground));
        }
        .sql-modal-body {
          padding: 20px;
          overflow-y: auto;
          flex: 1;
          min-height: 0;
        }
        .sql-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .sql-form-grid .full {
          grid-column: 1 / -1;
        }
        .sql-form-group label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 6px;
          color: hsl(var(--foreground));
        }
        .sql-form-group input, .sql-form-group select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          font-size: 13px;
        }
        .sql-form-group textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          font-size: 12px;
          font-family: monospace;
          resize: vertical;
        }
        .sql-form-group label.sql-toggle-label {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 10px;
          cursor: pointer;
          padding: 12px;
          background: hsl(var(--muted) / 0.3);
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          margin-top: 8px;
          font-size: 14px;
        }
        .sql-form-group label.sql-toggle-label svg {
          flex-shrink: 0;
        }
        .sql-label-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }
        .sql-file-import-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          font-size: 12px;
          color: hsl(var(--primary));
          background: hsl(var(--primary) / 0.1);
          border: 1px solid hsl(var(--primary) / 0.3);
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .sql-file-import-btn:hover {
          background: hsl(var(--primary) / 0.2);
        }
        .sql-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 16px 20px;
          border-top: 1px solid hsl(var(--border));
          background: hsl(var(--muted) / 0.3);
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

// ============================================================
// Edit Job Modal
// ============================================================

interface EditJobModalProps {
  job: SQLJob;
  onClose: () => void;
  onSave: (updates: { schedule?: string; status?: string }) => void;
  saving: boolean;
}

const EditJobModal: React.FC<EditJobModalProps> = ({ job, onClose, onSave, saving }) => {
  const [newSchedule, setNewSchedule] = useState(job.schedule || '');
  const [status, setStatus] = useState<string>(job.status || 'active');

  return (
    <div className="sql-modal-overlay">
      <div className="sql-modal">
        <div className="sql-modal-header">
          <h3><Settings className="w-5 h-5" /> Edit Job: {job.name}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="sql-modal-body">
          <div className="sql-form-grid">
            <div className="sql-form-group full">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>

            <div className="sql-form-group full">
              <label><Clock className="w-4 h-4" /> Schedule</label>
              <select value={newSchedule} onChange={(e) => setNewSchedule(e.target.value)}>
                {SCHEDULE_PRESETS.map(preset => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
              <p className="sql-hint" style={{ marginTop: '8px' }}>
                Current: {job.schedule || 'Manual (no schedule)'}
              </p>
            </div>

            <div className="sql-form-group full">
              <label>Query</label>
              <div style={{
                padding: '12px',
                background: 'hsl(220 13% 14%)',
                borderRadius: '6px',
                fontSize: '12px',
                fontFamily: 'monospace',
                color: 'hsl(220 13% 91%)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: '150px',
                overflow: 'auto'
              }}>
                {job.query || 'No query defined'}
              </div>
            </div>
          </div>
        </div>

        <div className="sql-modal-footer">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave({ schedule: newSchedule || undefined, status })}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SQLIngestionTab;
